import { useState } from "react";
import { Link } from "react-router-dom";
import SubmissionDeck3D from "../components/SubmissionDeck3D";
import { submissionsApi } from "../services/externalPlatformApi";

const EMPTY_FORM = {
  type: "MIX",
  artist_name: "",
  email: "",
  link_url: "",
  track_title: "",
  genre: "",
  bio: "",
};

const TYPES = ["MIX", "TRACK"];
const GENRES = ["House", "Tech House", "Deep House", "Minimal", "Afro House", "Melodic House"];

export default function SubmitMix() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setSaving(true);

    try {
      await submissionsApi.submitMix(form);
      setMessage("Submission received. It is now in the HLT admin review queue.");
      setForm(EMPTY_FORM);
    } catch {
      setError("The submission endpoint is not connected yet. Locally, the mock backend will accept this.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page space-y-8">
      <section className="overflow-hidden border border-white/10 bg-black/55">
        <div className="grid gap-3 p-5 sm:gap-6 sm:p-6 lg:grid-cols-[minmax(0,1fr),36rem] lg:items-center">
          <div>
        <p className="text-xs uppercase tracking-[0.28em] text-brand-500">HLT submissions</p>
        <h1 className="mt-2 font-display text-5xl leading-none text-white md:text-7xl">
          Submit your{" "}
          <span className="font-serifDisplay italic tracking-normal text-brand-500">sound</span>
        </h1>
        <p className="mt-4 max-w-3xl text-slate-300">
          Send a mix or track link for the HLT team to review. Approved submissions can move into
          roster consideration, event bookings and editorial support.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/roster" className="btn-secondary">
            View talent roster
          </Link>
            </div>
          </div>
          <SubmissionDeck3D />
        </div>
      </section>

      {message && <div className="border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-100">{message}</div>}
      {error && <div className="border border-amber-500/30 bg-amber-500/10 p-4 text-amber-100">{error}</div>}

      <section className="grid gap-6 lg:grid-cols-[1fr,22rem]">
        <form onSubmit={submit} className="border border-white/10 bg-[#111111] p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Submission type">
              <div className="grid grid-cols-2 gap-2">
                {TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={form.type === type ? "btn-primary" : "btn-secondary"}
                    onClick={() => update("type", type)}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Genre">
              <select className="input" value={form.genre} onChange={(event) => update("genre", event.target.value)}>
                <option value="">Select genre</option>
                {GENRES.map((genre) => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Artist name">
              <input className="input" value={form.artist_name} onChange={(event) => update("artist_name", event.target.value)} required />
            </Field>
            <Field label="Email">
              <input type="email" className="input" value={form.email} onChange={(event) => update("email", event.target.value)} required />
            </Field>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <Field label="Mix / track title">
              <input className="input" value={form.track_title} onChange={(event) => update("track_title", event.target.value)} required />
            </Field>
            <Field label="Listening link">
              <input type="url" className="input" value={form.link_url} onChange={(event) => update("link_url", event.target.value)} placeholder="https://soundcloud.com/..." required />
            </Field>
          </div>

          <div className="mt-4">
            <Field label="Short bio / context">
              <textarea className="textarea min-h-32" value={form.bio} onChange={(event) => update("bio", event.target.value)} />
            </Field>
          </div>

          <button className="btn-primary mt-5 w-full" disabled={saving}>
            {saving ? "Sending..." : "Send to HLT"}
          </button>
        </form>

        <aside className="border border-white/10 bg-black/35 p-5">
          <p className="text-xs uppercase tracking-[0.28em] text-brand-500">Review flow</p>
          <h2 className="mt-2 font-display text-4xl leading-none text-white">What happens next</h2>
          <div className="mt-5 space-y-4 text-sm leading-6 text-slate-300">
            <p>1. Your link lands in the Admin Submissions queue.</p>
            <p>2. HLT can approve, reject or add review notes.</p>
            <p>3. Strong submissions can be moved into roster, bookings or content opportunities.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</span>
      {children}
    </label>
  );
}
